/**
 * Shared US phone helpers.
 *
 * The UI hides the country code for US users, but the country code remains a
 * concept in the data model (US numbers are implicitly +1, and the intl-tel
 * surfaces still submit an E.164 value). These helpers standardize the
 * patient-facing display/validation on the `XXX-XXX-XXXX` format used by the
 * result-submission field.
 */

/**
 * Strip a value down to its digits.
 * @param {string} value
 * @returns {string}
 */
export const digitsOnly = (value = '') => String(value == null ? '' : value).replace(/\D/g, '');

/**
 * Normalize to at most 10 US digits, dropping a leading country code `1`.
 * @param {string} value
 * @returns {string}
 */
export const normalizeUsDigits = (value = '') => {
  let digits = digitsOnly(value);
  if (digits.length === 11 && digits.charAt(0) === '1') {
    digits = digits.slice(1);
  }
  return digits.slice(0, 10);
};

/**
 * Format a value as a US phone number (`XXX-XXX-XXXX`) as the user types,
 * inserting a dash after the 3rd and 6th digit.
 * @param {string} value
 * @returns {string}
 */
export const formatUsPhone = (value = '') => {
  const digits = normalizeUsDigits(value);

  if (digits.length <= 3) {
    return digits;
  }

  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }

  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/**
 * Validate that a value is a complete 10-digit US phone number.
 * @param {string} value
 * @returns {boolean}
 */
export const isValidUsPhone = (value = '') => normalizeUsDigits(value).length === 10;
